import { cloneElement, isValidElement, ReactElement, ReactNode, useMemo } from 'react';
import { Table as AntDesignTable } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnGroupType, ColumnsType, ColumnType, TablePaginationConfig } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { TbArrowsSort, TbChevronLeft, TbChevronRight, TbSortAscending, TbSortDescending } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export type PlatformTableSortDirection = 'ascend' | 'ascending' | 'descend' | 'descending' | null | undefined;

interface PlatformTableSortIconProps {
    direction: PlatformTableSortDirection;
}

export const PlatformTableSortIcon = ({ direction }: PlatformTableSortIconProps): JSX.Element => {
    const { token } = useAntDesignTheme();
    const normalizedDirection =
        direction === 'ascend' || direction === 'ascending'
            ? 'ascending'
            : direction === 'descend' || direction === 'descending'
              ? 'descending'
              : 'unsorted';
    const iconStyle = {
        color: normalizedDirection === 'unsorted' ? token.colorTextTertiary : token.colorPrimary,
    };
    const iconProps = {
        'aria-hidden': true,
        'data-platform-table-sort-direction': normalizedDirection,
        focusable: 'false',
        size: '0.875rem',
        style: iconStyle,
    } as const;

    if (normalizedDirection === 'ascending') {
        return <TbSortAscending {...iconProps} />;
    }
    if (normalizedDirection === 'descending') {
        return <TbSortDescending {...iconProps} />;
    }

    return <TbArrowsSort {...iconProps} />;
};

interface PaginationControlElementProps {
    children?: ReactNode;
}

type PaginationItemRender = NonNullable<TablePaginationConfig['itemRender']>;

const replacePaginationControlIcon = (element: ReactNode, icon: ReactNode) =>
    isValidElement<PaginationControlElementProps>(element)
        ? cloneElement(element as ReactElement<PaginationControlElementProps>, undefined, icon)
        : icon;

const createPaginationItemRender =
    (itemRender?: PaginationItemRender): PaginationItemRender =>
    (page, type, originalElement) => {
        const renderedElement = itemRender?.(page, type, originalElement) ?? originalElement;

        if (type === 'prev') {
            return replacePaginationControlIcon(
                renderedElement,
                <TbChevronLeft
                    aria-hidden
                    data-platform-pagination-icon='previous'
                    focusable='false'
                    size='0.875rem'
                />,
            );
        }
        if (type === 'next') {
            return replacePaginationControlIcon(
                renderedElement,
                <TbChevronRight aria-hidden data-platform-pagination-icon='next' focusable='false' size='0.875rem' />,
            );
        }

        return renderedElement;
    };

const isColumnGroup = <RecordType extends object>(
    column: ColumnGroupType<RecordType> | ColumnType<RecordType>,
): column is ColumnGroupType<RecordType> => 'children' in column;

const applyPlatformSorting = <RecordType extends object>(
    columns: ColumnsType<RecordType> | undefined,
    sortIconGap: number,
): ColumnsType<RecordType> =>
    (columns ?? []).map((column) => {
        if (isColumnGroup(column)) {
            return { ...column, children: applyPlatformSorting(column.children, sortIconGap) };
        }
        if (!column.sorter) {
            return column;
        }

        return {
            ...column,
            sortDirections: ['ascend', 'descend'] satisfies SortOrder[],
            sortIcon: ({ sortOrder }: { sortOrder: SortOrder }) => (
                <span
                    data-platform-table-sort-icon-container='true'
                    style={{ display: 'inline-flex', marginInlineStart: sortIconGap }}
                >
                    <PlatformTableSortIcon direction={sortOrder} />
                </span>
            ),
        };
    });

export type PlatformTableProps<RecordType extends object = Record<string, unknown>> = TableProps<RecordType>;

const PlatformTable = <RecordType extends object = Record<string, unknown>>({
    columns,
    pagination,
    ...props
}: PlatformTableProps<RecordType>): JSX.Element => {
    const { token } = useAntDesignTheme();
    const platformColumns = useMemo(() => applyPlatformSorting(columns, token.marginXXS), [columns, token.marginXXS]);
    const platformPagination = useMemo<TablePaginationConfig | false>(() => {
        if (pagination === false) {
            return false;
        }

        return {
            ...(pagination ?? {}),
            itemRender: createPaginationItemRender(pagination?.itemRender),
            style: {
                ...(pagination?.style ?? {}),
                columnGap: token.marginXXS,
                rowGap: token.marginXXS,
            },
        };
    }, [pagination, token.marginXXS]);

    return <AntDesignTable {...props} columns={platformColumns} pagination={platformPagination} />;
};

export default PlatformTable;
